import React, { Component } from 'react';
import { Alert, Button, Intent, Position, Tooltip } from "@blueprintjs/core";
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import Toast from 'helpers/toaster';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import qs from 'qs';
import moment from 'moment';
import * as DataList from 'components/DataList';

// Column and card layouts, shared with the page's loading skeleton.
export const USER_COLUMNS = [
  { area: 'person', label: 'Name', width: 'minmax(240px, 1.6fr)', bone: 'person' },
  { area: 'phone', label: 'Phone', width: 'minmax(140px, 1fr)', bone: 'single', icon: 'phone' },
  { area: 'role', label: 'Role and skills', width: 'minmax(150px, 1fr)' },
  { area: 'status', label: 'Status', width: '130px', bone: 'pill' },
  { area: 'actions', label: '', width: '76px', bone: 'none' },
];

export const USER_CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: ['person status actions', 'phone phone phone', 'role role role'],
  footer: ['role'],
};

export const USER_MIN_TABLE_WIDTH = 760;

// Invitations have no phone; who sent them and when they lapse matter more.
const INVITATION_COLUMNS = [
  { area: 'person', label: 'Invitee', width: 'minmax(240px, 1.6fr)', bone: 'person' },
  { area: 'role', label: 'Role', width: '130px', bone: 'single' },
  { area: 'invited', label: 'Invited by', width: 'minmax(170px, 1fr)', icon: 'envelope' },
  { area: 'expires', label: 'Expires', width: '130px', bone: 'single', icon: 'time' },
  { area: 'status', label: 'Status', width: '130px', bone: 'pill' },
  { area: 'actions', label: '', width: '104px', bone: 'none' },
];

const INVITATION_CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: ['person status actions', 'invited invited invited', 'expires expires expires', 'role role role'],
  footer: ['role'],
};

const INVITATION_MIN_TABLE_WIDTH = 900;

// Layout for a section, shared with the page's loading skeleton.
export const userListLayout = (section) => (section === 'invitations'
  ? { columns: INVITATION_COLUMNS, cards: INVITATION_CARDS, minTableWidth: INVITATION_MIN_TABLE_WIDTH }
  : { columns: USER_COLUMNS, cards: USER_CARDS, minTableWidth: USER_MIN_TABLE_WIDTH });

const isExpired = (invitation) => {
  const { expires_at: expiresAt } = invitation.attributes;
  return Boolean(expiresAt) && moment(expiresAt).isBefore(moment());
};

const inviterName = (invitation) => {
  const inviter = invitation.attributes.invited_by;
  if (!inviter) return null;
  return [inviter.first_name, inviter.last_name].filter(Boolean).join(' ') || inviter.email || null;
};

const ROLE_NOUNS = { admin: 'admins', technician: 'technicians' };

// The API filters by user type but not (yet) by status or text, so those two
// are applied here. Status is a no-op once the API filters it too.
export const filterUsers = (objects, { term, where = {} }) => {
  const needle = (term || '').trim().toLowerCase();
  const statuses = where.status || [];
  return objects.filter(({ attributes }) => {
    if (statuses.length && !attributes.is_invitation) {
      if (!statuses.includes(attributes.is_active ? 'active' : 'inactive')) return false;
    }
    if (!needle) return true;
    return [attributes.full_name, attributes.email, attributes.formatted_phone_number, attributes.phone]
      .some((value) => value && String(value).toLowerCase().includes(needle));
  });
};

// "29 admins and 64 technicians", for the list's summary line.
const roleBreakdown = (objects) => {
  const counts = objects.reduce((map, { attributes }) => (
    { ...map, [attributes.role]: (map[attributes.role] || 0) + 1 }
  ), {});
  const parts = Object.keys(ROLE_NOUNS)
    .filter((role) => counts[role])
    .map((role) => `${counts[role]} ${counts[role] === 1 ? ROLE_NOUNS[role].replace(/s$/, '') : ROLE_NOUNS[role]}`);
  return parts.length > 1 ? parts.join(' and ') : null;
};

const capitalize = (text) => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '');

// Initials avatar like DataList.Person, but showing the user's photo when
// they have one.
const UserPerson = ({ user, link }) => {
  const { avatar_url: avatar, email } = user.attributes;
  // Invitations without a name come back as full_name " ".
  const name = (user.attributes.full_name || '').trim();
  return (
    <div className="dl-person">
      <span className="dl-person__avatar" aria-hidden="true">
        {avatar ? (
          <img src={avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
        ) : DataList.initials(name || email)}
      </span>
      <span className="dl-person__text">
        {link ? (
          <DataList.RowLink href={link} label={`User ${name || email}`}>
            <span className="dl-person__name" title={name}>{name || email}</span>
          </DataList.RowLink>
        ) : (
          <span className="dl-person__name" title={name}>{name || email}</span>
        )}
        {name && email && <span className="dl-muted dl-truncate" title={email}>{email}</span>}
      </span>
    </div>
  );
};

const UserStatus = ({ user }) => {
  if (user.attributes.is_invitation) {
    if (user.attributes.status === 'accepted') return <DataList.Pill tone="green">Accepted</DataList.Pill>;
    return isExpired(user)
      ? <DataList.Pill tone="red">Expired</DataList.Pill>
      : <DataList.Pill tone="amber">Invite pending</DataList.Pill>;
  }
  return user.attributes.is_active
    ? <DataList.Pill tone="green">Active</DataList.Pill>
    : <DataList.Pill tone="grey">Inactive</DataList.Pill>;
};

@inject('dashboard', 'viewStore', 'userStore') @observer
class UsersTable extends Component {

  @observable isDeleteDialogVisible = false;
  @observable user;
  @observable invitationToCancel = null;
  @observable isCancellingInvitation = false;

  confirmCancelInvitation() {
    const { userStore, dashboard, viewStore } = this.props;
    const invitation = this.invitationToCancel;
    this.isCancellingInvitation = true;
    userStore.cancelInvitation(invitation.attributes.id)
      .then(() => {
        Toast.show({ icon: 'tick', message: `Invitation for ${invitation.attributes.email} cancelled`, intent: Intent.SUCCESS });
        dashboard.loadUsers({ section: viewStore.userSearch.section, where: viewStore.userSearch.where });
      })
      .catch((error) => {
        const message = (error && error.response && error.response.data && error.response.data.error) || 'Couldn\'t cancel the invitation. Try again.';
        Toast.show({ icon: 'warning-sign', message, intent: Intent.DANGER });
      })
      .then(() => {
        this.isCancellingInvitation = false;
        this.invitationToCancel = null;
      });
  }

  handleUserDeleteClick(user) {
    this.isDeleteDialogVisible = true;
    this.user = user;
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
    this.user = null;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.viewStore.deleteUser(this.user)
      .then(this.onSuccessfulDeleteUser.bind(this))
      .catch(this.onFailedUserDelete.bind(this))
  }

  onSuccessfulDeleteUser(response) {
    const { dashboard, viewStore } = this.props;
    Toast.show({ icon: 'thumbs-up', message: 'User deleted', intent: Intent.SUCCESS })
    this.user = null;
    this.isDeleteDialogVisible = false;
    dashboard.loadUsers({ section: viewStore.userSearch.section, where: viewStore.userSearch.where });
  }

  // Empty list's "Clear filters": everyone, any status, no search text.
  clearFilters() {
    const { viewStore } = this.props;
    const userSearch = { ...viewStore.userSearch, term: '', where: { role: [], status: [], skills: [] } };
    viewStore.userSearch = userSearch;
    viewStore.bumpListFilters();
    viewStore.users();
  }

  onFailedUserDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete user!', intent: Intent.DANGER })
  }

  resendInvite(invitation){
    this.props.userStore.resendUserInvite(invitation.attributes.id)
      .then(() => {
        Toast.show({ message: "Invite resent", intent: Intent.SUCCESS })
      })
  }

  copyInvitationLink(invitation){
    return(`${window.location.origin}/users/invitation/${invitation.attributes.token}`);
  }

  userLink(user) {
    const { viewStore } = this.props;
    if (viewStore.userSearch.section === 'invitations' || user.attributes.is_invitation) return null;
    return `/dashboard/users/${user.attributes.id}/show?${qs.stringify(viewStore.userShowSearch, { encode: false })}`;
  }

  // Same markup as DataList.Actions; the copy button needs CopyToClipboard
  // around it, which DataList.Actions can't do.
  renderInvitationActions(user) {
    return (
      <div className="dl-actions dl-raised">
        <Tooltip content="Re-send invite" position={Position.TOP}>
          <Button minimal={true} small={true} icon="envelope" aria-label="Re-send invite" onClick={this.resendInvite.bind(this, user)} />
        </Tooltip>
        {/* CopyToClipboard puts its click handler on its direct child, so the
            Button must be that child; Tooltip wouldn't pass the click on. */}
        <Tooltip content="Copy invitation link" position={Position.TOP}>
          <CopyToClipboard
            text={this.copyInvitationLink(user)}
            onCopy={() => Toast.show({ message: "Invitation link copied", intent: Intent.SUCCESS })}
          >
            <Button minimal={true} small={true} icon="link" aria-label="Copy invitation link" />
          </CopyToClipboard>
        </Tooltip>
        {user.attributes.status !== 'accepted' && (
          <Tooltip content="Cancel invitation" position={Position.TOP}>
            <Button
              minimal={true}
              small={true}
              icon="trash"
              intent={Intent.DANGER}
              aria-label="Cancel invitation"
              onClick={() => { this.invitationToCancel = user; }}
            />
          </Tooltip>
        )}
      </div>
    );
  }

  render() {
    let {
      viewStore,
      dashboard: {
        users: {
          objects
        }
      }
    } = this.props;
    const { userSearch } = viewStore;
    const invitations = userSearch.section === 'invitations';
    const noun = invitations ? 'invitations' : 'users';
    const rows = filterUsers(objects, userSearch);
    const term = (userSearch.term || '').trim();
    const filters = invitations ? [] : DataList.activeFilters(userSearch.where, { role: 'user type', status: 'status', skills: 'skills' });

    if (objects.length === 0 && filters.length === 0) {
      return (
        <DataList.Empty
          noun={noun}
          title={invitations ? 'No pending invitations' : 'No users yet'}
          message={invitations
            ? 'Everyone you invited has joined. Invite someone new to see them here.'
            : 'Invite your team to give them access to FieldNexus.'}
          extraAction={{ label: 'Invite user', icon: 'new-person', onClick: () => viewStore.inviteUsers(), primary: true }}
        />
      );
    }

    if (rows.length === 0) {
      return (
        <DataList.Empty
          noun={noun}
          term={term}
          filters={filters}
          onClearFilters={this.clearFilters.bind(this)}
        />
      );
    }

    const filtered = rows.length !== objects.length;
    return (
      <div style={{ width: '100%' }}>
        <DataList.List
          {...userListLayout(userSearch.section)}
          total={filtered ? `${rows.length} of ${objects.length}` : rows.length}
          noun={noun}
          note={invitations ? null : roleBreakdown(rows)}
        >
          {rows.map((user) => {
            const attributes = user.attributes;
            const phone = attributes.formatted_phone_number;
            return (
              <DataList.Row key={user.id}>
                <DataList.Cell area="person">
                  <UserPerson user={user} link={this.userLink(user)} />
                </DataList.Cell>
                {invitations ? (
                  <React.Fragment>
                    <DataList.Cell area="invited">
                      <DataList.Lines
                        primary={<React.Fragment><DataList.CardOnly>Invited by </DataList.CardOnly>{inviterName(user) || 'Unknown'}</React.Fragment>}
                        secondary={attributes.created_at ? `Sent ${moment(attributes.created_at).format('MMM D, YYYY')}` : null}
                        strong={false}
                      />
                    </DataList.Cell>
                    <DataList.Cell area="expires">
                      {attributes.expires_at ? (
                        <DataList.Lines
                          primary={<React.Fragment><DataList.CardOnly>{isExpired(user) ? 'Expired ' : 'Expires '}</DataList.CardOnly>{moment(attributes.expires_at).format('MMM D, YYYY')}</React.Fragment>}
                          secondary={moment(attributes.expires_at).fromNow()}
                          strong={false}
                        />
                      ) : <DataList.Muted>No expiry</DataList.Muted>}
                    </DataList.Cell>
                  </React.Fragment>
                ) : (
                  <DataList.Cell area="phone">
                    {phone
                      ? <DataList.Lines primary={phone} strong={false} />
                      : <DataList.Muted>No phone</DataList.Muted>}
                  </DataList.Cell>
                )}
                <DataList.Cell area="role">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Role: </DataList.CardOnly>{capitalize(attributes.role) || '—'}</React.Fragment>}
                    secondary={!invitations && attributes.skills && attributes.skills.length ? attributes.skills.join(', ') : null}
                    strong={false}
                    title={attributes.role}
                  />
                </DataList.Cell>
                <DataList.Cell area="status">
                  <UserStatus user={user} />
                </DataList.Cell>
                <DataList.Cell area="actions">
                  {attributes.is_invitation ? this.renderInvitationActions(user) : (
                    <DataList.Actions
                      items={[
                        { icon: 'edit', label: 'Edit user', onClick: () => viewStore.userShow({ uuid: attributes.id }) },
                        {
                          icon: 'trash',
                          label: user.id === viewStore.currentUser.id ? 'You can\'t delete yourself' : 'Delete user',
                          intent: 'danger',
                          disabled: user.id === viewStore.currentUser.id,
                          onClick: () => this.handleUserDeleteClick(user),
                        },
                      ]}
                    />
                  )}
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Are you sure you want to delete <b>{this.user && this.user.attributes.full_name}</b> user?
          </p>
        </Alert>
        <Alert
          cancelButtonText="Keep invitation"
          confirmButtonText="Cancel invitation"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={Boolean(this.invitationToCancel)}
          loading={this.isCancellingInvitation}
          onCancel={() => { this.invitationToCancel = null; }}
          onConfirm={this.confirmCancelInvitation.bind(this)}
        >
          <p>
            Cancel the invitation for <b>{this.invitationToCancel && this.invitationToCancel.attributes.email}</b>?
            The link in their email will stop working. You can invite them again later.
          </p>
        </Alert>
      </div>
    )
  }
}

export default UsersTable;
